import {
  ALIGNS,
  POSITIONS,
  POSITION_DIRECTIONS,
  TOOLTIP_ARROW_SIZE,
  TOOLTIP_PADDING,
  TOOLTIP_TOTAL_PADDING,
} from './const';

export const FOCUSABLE_SELECTOR =
  'a,frame,iframe,input:not([type=hidden]):not(:disabled),select:not(:disabled),textarea:not(:disabled),button:not(:disabled),*[tabindex]';

export const isPositionLeft = (p) => p === POSITIONS.LEFT;

export const isPositionRight = (p) => p === POSITIONS.RIGHT;

export const isPositionBottom = (p) => p === POSITIONS.BOTTOM;

export const isPositionTop = (p) => p === POSITIONS.TOP;

export const isVertical = (p) => isPositionTop(p) || isPositionBottom(p);

export const isHorizontal = (p) => isPositionLeft(p) || isPositionRight(p);

export const isAlignCenter = (a) => a === ALIGNS.CENTER;

export const isAlignStart = (a) => a === ALIGNS.START;

export const isAlignEnd = (a) => a === ALIGNS.END;

export const resolveContainerAlign = ({
  align,
  position,
  containerTop,
  containerLeft,
  containerHeight,
  containerWidth,
  tooltipHeight,
  tooltipWidth,
}) => {
  if (isAlignCenter(align)) {
    if (isHorizontal(position)) {
      return {
        top: `${Math.floor(
          containerTop + containerHeight / 2 - tooltipHeight / 2,
        )}px`,
      };
    }
    if (isVertical(position)) {
      return {
        left: `${Math.floor(
          containerLeft + containerWidth / 2 - tooltipWidth / 2,
        )}px`,
      };
    }
    return null;
  }
  if (isAlignStart(align)) {
    if (isHorizontal(position)) {
      return {
        top: `${Math.floor(
          containerTop - parseFloat(TOOLTIP_TOTAL_PADDING),
        )}px`,
      };
    }
    if (isVertical(position)) {
      return {
        left: `${Math.floor(
          containerLeft +
            containerWidth / 2 -
            parseFloat(TOOLTIP_ARROW_SIZE) * 3,
        )}px`,
      };
    }
  } else if (isAlignEnd(align)) {
    if (isHorizontal(position)) {
      return {
        top: `${Math.floor(
          containerTop -
            tooltipHeight +
            containerHeight +
            parseFloat(TOOLTIP_TOTAL_PADDING),
        )}px`,
      };
    }
    if (isVertical(position)) {
      return {
        left: `${Math.floor(
          containerLeft +
          containerWidth -
          tooltipWidth +
          parseFloat(TOOLTIP_ARROW_SIZE) * 3,
        )}px`
      };
    }
  }
  return null;
};

export const resolveContainerPosition = ({
  position,
  containerTop,
  containerLeft,
  containerHeight,
  containerWidth,
  tooltipHeight,
  tooltipWidth,
}) => {
  if (isPositionTop(position)) {
    /*
      Needed, otherwise the top position is incorrectly positioned due to missing 8 pixels.
      It's produced by tooltipPadding.js helper that changes the padding based on the actual height -
      if it's one-line or multi-line.
     */
    return {
      top: `${Math.floor(
        containerTop - tooltipHeight - parseFloat(TOOLTIP_ARROW_SIZE) + 3,
      )}px`,
    };
  }
  if (isPositionBottom(position)) {
    return {
      top: `${Math.floor(
        containerTop + containerHeight + parseFloat(TOOLTIP_ARROW_SIZE),
      )}px`,
    };
  }
  if (isPositionRight(position)) {
    return {
      left: `${Math.floor(
        containerLeft + containerWidth + parseFloat(TOOLTIP_ARROW_SIZE),
      )}px`,
    };
  }
  if (isPositionLeft(position)) {
    return {
      left: `${Math.floor(
        containerLeft - tooltipWidth - parseFloat(TOOLTIP_ARROW_SIZE),
      )}px`,
    };
  }
  return null;
};

export const resolveTooltipArrowAlign = ({
  position,
  align,
  tooltipWidth,
  tooltipHeight,
}) => {
  if (isVertical(position)) {
    if (isAlignCenter(align)) {
      return {
        left: `${Math.floor(
          tooltipWidth / 2 - parseFloat(TOOLTIP_ARROW_SIZE),
        )}px`,
      };
    }
    if (isAlignStart(align)) {
      return {
        left: `${parseFloat(TOOLTIP_PADDING)}px`,
      };
    }
    if (isAlignEnd(align)) {
      return {
        right: `${parseFloat(TOOLTIP_PADDING)}px`,
      };
    }
  } else if (isHorizontal(position)) {
    if (isAlignCenter(align)) {
      return {
        top: `${Math.floor(
          tooltipHeight / 2 - parseFloat(TOOLTIP_ARROW_SIZE),
        )}px`,
      };
    }
    if (isAlignStart(align)) {
      return {
        top: `${parseFloat(TOOLTIP_PADDING)}px`,
      };
    }
    if (isAlignEnd(align)) {
      return {
        bottom: `${parseFloat(TOOLTIP_PADDING)}px`,
      };
    }
  }
  return null;
};

export const resolveTooltipArrowPosition = ({ position }) => {
  const cssPosition =
    (isPositionTop(position) && 'bottom') ||
    (isPositionBottom(position) && 'top') ||
    (isPositionLeft(position) && 'right') ||
    (isPositionRight(position) && 'left');
  return `${cssPosition}: -${parseFloat(TOOLTIP_ARROW_SIZE)}px`;
};

export const getPossiblePositions = ({
  containerTopPure,
  containerLeftPure,
  containerHeight,
  containerWidth,
  tooltipWidth,
  tooltipHeight,
  windowWidth,
  windowHeight,
}) => ({
  [POSITIONS.LEFT]: containerLeftPure - tooltipWidth - TOOLTIP_ARROW_SIZE > 0,
  [POSITIONS.RIGHT]:
    containerLeftPure + containerWidth + tooltipWidth + TOOLTIP_ARROW_SIZE <
    windowWidth,
  [POSITIONS.TOP]: containerTopPure - tooltipHeight > 0,
  [POSITIONS.BOTTOM]:
    containerTopPure + containerHeight + tooltipHeight < windowHeight,
});

export const calculateTooltipPosition = (positions, dimensions) => {
  const possiblePositions = getPossiblePositions(dimensions);
  const position = positions.find((p) => possiblePositions[p]);
  if (typeof position === 'string') {
    return position;
  }
  return null;
};

const getPositionDirection = (position) => {
  if (isVertical(position)) {
    return POSITION_DIRECTIONS.VERTICAL;
  }
  if (isHorizontal(position)) {
    return POSITION_DIRECTIONS.HORIZONTAL;
  }
  return null;
};

export const getPossibleAligns = ({
  containerHeight,
  containerWidth,
  tooltipWidth,
  tooltipHeight,
  windowWidth,
  windowHeight,
  containerLeftPure,
  containerTopPure,
}) => ({
  [POSITION_DIRECTIONS.VERTICAL]: {
    [ALIGNS.START]:
      containerLeftPure + containerWidth / 2 - TOOLTIP_TOTAL_PADDING > 0 &&
      containerLeftPure +
        containerWidth / 2 -
        TOOLTIP_TOTAL_PADDING +
        tooltipWidth <
        windowWidth,
    [ALIGNS.CENTER]:
      containerLeftPure + containerWidth / 2 - tooltipWidth / 2 > 0 &&
      containerLeftPure + containerWidth / 2 + tooltipWidth / 2 < windowWidth,
    [ALIGNS.END]:
      containerLeftPure + containerWidth + TOOLTIP_TOTAL_PADDING <
        windowWidth &&
      containerLeftPure +
        containerWidth / 2 +
        TOOLTIP_TOTAL_PADDING -
        tooltipWidth >
        0,
  },
  [POSITION_DIRECTIONS.HORIZONTAL]: {
    [ALIGNS.START]:
      containerTopPure + containerHeight / 2 - TOOLTIP_TOTAL_PADDING > 0 &&
      containerTopPure +
        containerHeight / 2 +
        (tooltipHeight - TOOLTIP_TOTAL_PADDING) <
        windowHeight,
    [ALIGNS.CENTER]:
      containerTopPure + containerHeight / 2 - tooltipHeight / 2 > 0 &&
      containerTopPure + containerHeight / 2 + tooltipHeight / 2 < windowHeight,
    [ALIGNS.END]:
      containerTopPure +
        containerHeight +
        TOOLTIP_TOTAL_PADDING -
        tooltipHeight >
        0 &&
      containerTopPure + containerHeight / 2 + TOOLTIP_TOTAL_PADDING <
        windowHeight,
  },
});

export const calculateTooltipAlign = (position, aligns, dimensions) => {
  if (!position) {
    return null;
  }

  const direction = getPositionDirection(position);
  if (direction) {
    const possibleAligns = getPossibleAligns(dimensions);
    const align = aligns.find((a) => possibleAligns[direction][a]);
    if (typeof align === 'string') {
      return align;
    }
    return null;
  }
  return null;
};

export const getPosition = (positionsObject, preferredPosition) => {
  const positions = Object.keys(positionsObject).map((k) => positionsObject[k]);
  if (!preferredPosition) return positions;
  return [
    preferredPosition,
    ...positions.filter((p) => p !== preferredPosition),
  ];
};

export const getAlign = (preferredAlign) => {
  const aligns = Object.keys(ALIGNS).map((k) => ALIGNS[k]);
  if (!preferredAlign) return aligns;
  return [preferredAlign, ...aligns.filter((p) => p !== preferredAlign)];
};
